import React, { useState, useCallback } from "react";
import axios from "axios";
import confirmAction from "../../utils/confirm";

const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;

const axiosConfig = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken() },
});

const axiosMultipart = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken(), "Content-Type": "multipart/form-data" },
});

const SymbolEditor = ({ symbol, locales, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, locale }) => {
  const [label, setLabel] = useState(symbol.label || {});
  const [image, setImage] = useState(symbol.image);
  const [saving, setSaving] = useState(false);
  const [activeLocale, setActiveLocale] = useState(locales[0]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { label };
      const res = await axios.patch(`/${locale}/admin/symbols_overview/team_symbols/${symbol.id}`, data, axiosConfig());
      onUpdate(res.data);
    } catch (e) {
      console.error("Failed to save symbol:", e);
    }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    Object.entries(label).forEach(([k, v]) => formData.append(`label[${k}]`, v));
    try {
      const res = await axios.patch(`/${locale}/admin/symbols_overview/team_symbols/${symbol.id}`, formData, axiosMultipart());
      setImage(res.data.image);
      onUpdate(res.data);
    } catch (e) {
      console.error("Failed to upload image:", e);
    }
    e.target.value = "";
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Symbol #{symbol.position + 1}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(symbol.id))}>Delete</button>
          </div>
        </div>

        {/* Image preview + upload */}
        <div className="form-group">
          <label>Image</label>
          {image && (
            <div className="mb-2">
              <img src={image.url} alt={image.filename} className="img-fluid rounded" style={{ maxHeight: 200 }} />
            </div>
          )}
          <input type="file" className="form-control-file" accept="image/*" onChange={handleImageUpload} />
        </div>

        {/* Locale tabs */}
        <ul className="nav nav-tabs mt-3">
          {locales.map((loc) => (
            <li className="nav-item" key={loc}>
              <a
                className={`nav-link ${activeLocale === loc ? "active" : ""}`}
                href="#"
                onClick={(e) => { e.preventDefault(); setActiveLocale(loc); }}
              >
                {loc.toUpperCase()}
              </a>
            </li>
          ))}
        </ul>
        <div className="border border-top-0 p-3 mb-3">
          <div className="form-group">
            <label>Label ({activeLocale.toUpperCase()})</label>
            <input
              type="text" className="form-control"
              value={label[activeLocale] || ""}
              onChange={(e) => setLabel({ ...label, [activeLocale]: e.target.value })}
            />
          </div>
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Symbol"}
        </button>
      </div>
    </div>
  );
};

const SymbolsManager = ({ initialSymbols, locale, locales }) => {
  const [symbols, setSymbols] = useState(initialSymbols || []);
  const [adding, setAdding] = useState(false);

  const reorderSymbols = useCallback(async (newSymbols) => {
    setSymbols(newSymbols);
    const ids = newSymbols.map((s) => s.id);
    try {
      await axios.patch(`/${locale}/admin/symbols_overview/team_symbols/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder symbols:", e);
    }
  }, [locale]);

  const moveItem = (list, index, direction) => {
    const newList = [...list];
    const swapIndex = index + direction;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    reorderSymbols(newList);
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      const data = { label: {} };
      const res = await axios.post(`/${locale}/admin/symbols_overview/team_symbols`, data, axiosConfig());
      setSymbols([...symbols, res.data]);
    } catch (e) {
      console.error("Failed to create symbol:", e);
    }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/symbols_overview/team_symbols/${id}`, axiosConfig());
      setSymbols(symbols.filter((s) => s.id !== id));
    } catch (e) {
      console.error("Failed to delete symbol:", e);
    }
  };

  const handleUpdate = (updatedSymbol) => {
    setSymbols(symbols.map((s) => (s.id === updatedSymbol.id ? { ...s, ...updatedSymbol } : s)));
  };

  return (
    <div className="mt-3">
      {symbols.map((symbol, index) => (
        <SymbolEditor
          key={symbol.id}
          symbol={symbol}
          locales={locales}
          locale={locale}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onMoveUp={() => moveItem(symbols, index, -1)}
          onMoveDown={() => moveItem(symbols, index, 1)}
          isFirst={index === 0}
          isLast={index === symbols.length - 1}
        />
      ))}

      <div className="card mb-4">
        <div className="card-body text-center">
          <button className="btn btn-success" onClick={handleAdd} disabled={adding}>
            {adding ? "Adding..." : "Add New Symbol"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SymbolsManager;
